import { ref, computed, onMounted, onUnmounted } from "vue";

// Standard PowerPoint dimensions in EMUs (914400 EMUs = 1 inch)
const DEFAULT_SLIDE_WIDTH = 12192000; // ~13.33 inches (widescreen 16:9)
const DEFAULT_SLIDE_HEIGHT = 6858000; // ~7.5 inches
const REFERENCE_WIDTH = 960;

/**
 * Composable for slide scaling: converts EMU coordinates to pixels
 * and tracks container width via ResizeObserver.
 *
 * @param {import('vue').Ref<Object|null>} structure - Slide structure with optional slide_width/slide_height
 */
export function useSlideScaling(structure) {
  const containerRef = ref(null);
  const containerWidth = ref(0);

  const slideWidthEmu = computed(() => structure.value?.slide_width || DEFAULT_SLIDE_WIDTH);
  const slideHeightEmu = computed(() => structure.value?.slide_height || DEFAULT_SLIDE_HEIGHT);

  const referenceWidth = REFERENCE_WIDTH;
  const referenceHeight = computed(() => referenceWidth * (slideHeightEmu.value / slideWidthEmu.value));

  const scaleFactor = computed(() => containerWidth.value / referenceWidth);

  /**
   * Convert EMU position/size to pixel values relative to the reference dimensions.
   */
  function emuToPixels(position, size) {
    const left = ((position?.x || 0) / slideWidthEmu.value) * referenceWidth;
    const top = ((position?.y || 0) / slideHeightEmu.value) * referenceHeight.value;
    const width = ((size?.width || 100000) / slideWidthEmu.value) * referenceWidth;
    const height = ((size?.height || 100000) / slideHeightEmu.value) * referenceHeight.value;
    return { left, top, width, height };
  }

  // ResizeObserver to track container width
  let resizeObserver = null;

  onMounted(() => {
    if (containerRef.value) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          if (width > 0) {
            containerWidth.value = width;
          }
        }
      });
      resizeObserver.observe(containerRef.value);
    }
  });

  onUnmounted(() => {
    resizeObserver?.disconnect();
  });

  return {
    containerRef,
    referenceWidth,
    referenceHeight,
    scaleFactor,
    emuToPixels,
  };
}
